import React from 'react';
import { Insight } from '../utils/insightsAnalyzer';
import './InsightsPanel.css';

interface InsightsPanelProps {
  insights: Insight[];
  chartTitle: string;
}

const InsightsPanel: React.FC<InsightsPanelProps> = ({ insights, chartTitle }) => {
  if (insights.length === 0) {
    return (
      <div className="insights-panel panel-surface">
        <div className="insights-header">
          <h4>Insights</h4>
          <span>{chartTitle}</span>
        </div>
        <p className="empty-state-copy">No high-confidence insights were detected for this view.</p>
      </div>
    );
  }

  return (
    <div className="insights-panel panel-surface">
      <div className="insights-header">
        <h4>Insights</h4>
        <span>{chartTitle}</span>
      </div>

      <div className="insights-list">
        {insights.map((insight) => (
          <article key={insight.id} className="insight-card">
            <header>
              <div>
                <h5>{insight.title}</h5>
                <p>{insight.description}</p>
              </div>
              <span className={`confidence-pill ${insight.confidence >= 85 ? 'high' : insight.confidence >= 70 ? 'medium' : 'low'}`}>
                {insight.confidence}%
              </span>
            </header>

            {insight.method && (
              <div className="insight-block">
                <strong>Method</strong>
                <p>{insight.method}</p>
              </div>
            )}

            {insight.evidence && insight.evidence.length > 0 && (
              <div className="insight-block">
                <strong>Evidence</strong>
                <ul>
                  {insight.evidence.map((entry) => (
                    <li key={`${insight.id}-${entry.metric}`}>
                      {entry.metric}: {entry.value}
                      {entry.threshold ? ` (threshold ${entry.threshold})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {insight.assumptions && insight.assumptions.length > 0 && (
              <div className="insight-block">
                <strong>Assumptions</strong>
                <ul>
                  {insight.assumptions.map((assumption) => <li key={assumption}>{assumption}</li>)}
                </ul>
              </div>
            )}

            {insight.limitations && insight.limitations.length > 0 && (
              <div className="insight-block">
                <strong>Limitations</strong>
                <ul>
                  {insight.limitations.map((limitation) => <li key={limitation}>{limitation}</li>)}
                </ul>
              </div>
            )}

            {insight.citations && insight.citations.length > 0 && (
              <div className="insight-block citations">
                <strong>Citations</strong>
                <ul>
                  {insight.citations.map((citation) => (
                    <li key={citation.id}>
                      <span>{citation.label}</span>
                      <small>{citation.detail}</small>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {insight.recommendation && (
              <div className="insight-block recommendation">
                <strong>Recommendation</strong>
                <p>{insight.recommendation}</p>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
};

export default InsightsPanel;
